import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Skeleton,
  Alert
} from '@mui/material';
import {
  Quiz as QuizIcon,
  Person as PersonIcon,
  Payments as PaymentsIcon,
  EmojiEvents as EmojiEventsIcon,
  TrendingUp as TrendingUpIcon,
  Leaderboard as LeaderboardIcon
} from '@mui/icons-material';
import { format } from 'date-fns';

import questionService from '../../services/question.service';
import leaderboardService from '../../services/leaderboard.service';
import { AuthContext } from '../../context/AuthContext';

function Dashboard() {
  const { user } = useContext(AuthContext);
  const [todayQuestion, setTodayQuestion] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Fetch today's question
        const questionResponse = await questionService.getTodayQuestion();
        setTodayQuestion(questionResponse.data);
        
        // Fetch today's leaderboard
        const leaderboardResponse = await leaderboardService.getDailyLeaderboard();
        setLeaderboard(leaderboardResponse.data.leaderboard);
      } catch (err) {
        console.error('Dashboard data fetch error:', err);
        setError('Failed to load dashboard data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchDashboardData();
  }, []);

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Welcome back, {user?.name || 'Admin'}! Here's an overview of today's trivia game.
        </Typography>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {/* Today's Question */}
      <Paper elevation={0} sx={{ p: 3, mb: 4, borderRadius: 2, border: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <QuizIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h6">Today's Question</Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />
        
        {loading ? (
          <Box sx={{ mb: 2 }}>
            <Skeleton variant="text" width="100%" height={30} />
            <Skeleton variant="text" width="80%" height={20} />
            <Skeleton variant="text" width="80%" height={20} />
            <Skeleton variant="text" width="80%" height={20} />
            <Skeleton variant="text" width="80%" height={20} />
          </Box>
        ) : todayQuestion ? (
          <Box>
            <Typography variant="body1" fontWeight="bold" gutterBottom>
              {todayQuestion.question_text}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              A: {todayQuestion.option_a}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              B: {todayQuestion.option_b}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              C: {todayQuestion.option_c}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              D: {todayQuestion.option_d}
            </Typography>
            <Typography variant="body2" color="primary" fontWeight="bold">
              Correct Answer: {todayQuestion.correct_answer}
            </Typography>
          </Box>
        ) : (
          <Alert severity="warning">
            No question set for today. 
            <Button 
              component={Link} 
              to="/questions/create" 
              color="primary" 
              size="small" 
              sx={{ ml: 1 }}
            >
              Create Question
            </Button>
          </Alert>
        )}
      </Paper>
      
      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                  <PersonIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6">Participants</Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {loading ? <Skeleton width={50} /> : leaderboard.length}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Total participants today
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                  <PaymentsIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6">Revenue</Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {loading ? (
                      <Skeleton width={80} />
                    ) : (
                      `${leaderboard.reduce((acc, curr) => acc + curr.entry_count, 0) * 2} GHS`
                    )}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Estimated revenue today (GHS 2 per entry)
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: 'warning.main', mr: 2 }}>
                  <TrendingUpIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6">Entries</Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {loading ? (
                      <Skeleton width={50} />
                    ) : (
                      leaderboard.reduce((acc, curr) => acc + curr.entry_count, 0)
                    )}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Total entries submitted today
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Leaderboard */}
      <Card sx={{ mb: 4 }}>
        <CardHeader 
          title="Today's Leaderboard" 
          subheader={`Top ${leaderboard.length} participants as of ${format(new Date(), 'PPp')}`}
          action={
            <Button 
              component={Link} 
              to="/leaderboard" 
              color="primary"
            >
              View All
            </Button>
          }
        />
        <Divider />
        <CardContent>
          {loading ? (
            <Box>
              {[...Array(5)].map((_, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
                  <Box sx={{ width: '100%' }}>
                    <Skeleton variant="text" width="60%" height={25} />
                    <Skeleton variant="text" width="30%" height={20} />
                  </Box>
                </Box>
              ))}
            </Box>
          ) : leaderboard.length > 0 ? (
            <List sx={{ width: '100%', p: 0 }}>
              {leaderboard.map((item, index) => (
                <ListItem 
                  key={item.user_id} 
                  divider={index < leaderboard.length - 1}
                  sx={{ 
                    backgroundColor: index < 3 ? 'rgba(255, 82, 82, 0.05)' : 'transparent',
                    borderRadius: 1
                  }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ 
                      bgcolor: index === 0 ? 'warning.main' : 
                                index === 1 ? 'grey.400' : 
                                index === 2 ? 'brown.300' : 'primary.main' 
                    }}>
                      {index + 1}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText 
                    primary={
                      <Typography variant="body1" fontWeight={index < 3 ? 'bold' : 'normal'}>
                        {item.name || item.msisdn}
                      </Typography>
                    }
                    secondary={`${item.total_points} points (${item.entry_count} entries)`} 
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Alert severity="info">
              No entries recorded for today's game session yet.
            </Alert>
          )}
        </CardContent>
      </Card>
      
      {/* Quick Actions */}
      <Paper sx={{ p: 3, borderRadius: 2, border: '1px solid #e0e0e0' }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Quick Actions</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              component={Link}
              to="/questions/create"
              fullWidth
              variant="outlined"
              color="primary"
              startIcon={<QuizIcon />}
              sx={{ py: 1 }}
            >
              New Question
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              component={Link}
              to="/leaderboard"
              fullWidth
              variant="outlined"
              color="secondary"
              startIcon={<LeaderboardIcon />}
              sx={{ py: 1 }}
            >
              View Leaderboard
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              component={Link}
              to="/winners"
              fullWidth
              variant="outlined"
              color="success"
              startIcon={<EmojiEventsIcon />}
              sx={{ py: 1 }}
            >
              Select Winners
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              component={Link}
              to="/users"
              fullWidth
              variant="outlined"
              startIcon={<PersonIcon />}
              sx={{ py: 1 }}
            >
              Manage Users
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
}

export default Dashboard;